import { useLocation, Navigate, Link } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Home, Shield, FileText, Phone, ArrowRight } from "lucide-react";

// Map of accented/incorrect URLs to correct slugs
const REDIRECTS: Record<string, string> = {
  // Inspeções page variants
  "/inspecoes": "/inspecoes-periodicas-elevadores",
  "/inspeções": "/inspecoes-periodicas-elevadores",
  "/inspecoes-periodicas": "/inspecoes-periodicas-elevadores",
  "/inspeções-periodicas": "/inspecoes-periodicas-elevadores",
  "/inspeções-periódicas": "/inspecoes-periodicas-elevadores",
  "/inspeções-periódicas-elevadores": "/inspecoes-periodicas-elevadores",
  "/inspeções periódicas de elevadores": "/inspecoes-periodicas-elevadores",
  // Agendar diagnostico variants
  "/agendar-diagnóstico": "/agendar-diagnostico",
  "/agendardiagnostico": "/agendar-diagnostico",
  "/agendar_diagnostico": "/agendar-diagnostico",
  // Contactos variants (Brazilian Portuguese vs European Portuguese)
  "/contatos": "/contactos",
  "/contato": "/contactos",
  "/contacto": "/contactos",
};

const quickLinks = [
  {
    title: "Página Inicial",
    description: "Voltar ao início",
    href: "/",
    icon: Home,
  },
  {
    title: "Auditoria Técnica",
    description: "Avaliação independente de elevadores",
    href: "/auditoria-tecnica",
    icon: Shield,
  },
  {
    title: "Auditoria de Contratos",
    description: "Análise de contratos de manutenção",
    href: "/auditoria-contrato-manutencao-elevadores",
    icon: FileText,
  },
  {
    title: "Contactos",
    description: "Entre em contacto connosco",
    href: "/contactos",
    icon: Phone,
  },
];

const NotFound = () => {
  const location = useLocation();
  
  // Check if current path matches any redirect (case-insensitive, decoded)
  const redirectTo = useMemo(() => {
    const decodedPath = decodeURIComponent(location.pathname).toLowerCase();
    
    // Direct match
    if (REDIRECTS[decodedPath]) {
      return REDIRECTS[decodedPath];
    }
    
    // Check if path contains "inspe" (catches all inspection page variants)
    if (decodedPath.includes("inspe") && decodedPath.includes("elev")) {
      return "/inspecoes-periodicas-elevadores";
    }
    
    // Check for partial matches
    for (const [pattern, target] of Object.entries(REDIRECTS)) {
      if (decodedPath.includes(pattern.replace("/", ""))) {
        return target;
      }
    }
    
    return null;
  }, [location.pathname]);

  useEffect(() => {
    if (!redirectTo) {
      console.error("404 Error: User attempted to access non-existent route:", location.pathname);
    }
  }, [location.pathname, redirectTo]);

  // Redirect if match found
  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <>
      <Helmet>
        <title>Página Não Encontrada | ElevAdvisor</title>
        <meta name="description" content="A página que procura não foi encontrada. Navegue para os nossos serviços principais ou contacte-nos." />
        <meta name="robots" content="noindex, follow" />
        <meta name="googlebot" content="noindex, follow" />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader />
        
        <main className="flex-1 flex items-center justify-center py-16 md:py-24">
          <div className="container max-w-3xl">
            <div className="text-center mb-12">
              {/* Error Code */}
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-muted mb-6">
                <span className="text-3xl font-bold text-muted-foreground">404</span>
              </div>
              
              {/* Heading */}
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                Página Não Encontrada
              </h1>
              
              {/* Description */}
              <p className="text-muted-foreground max-w-md mx-auto">
                O endereço que procura não existe ou foi movido. 
                Utilize os links abaixo para navegar no site.
              </p>
            </div>

            {/* Quick Links Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
              {quickLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="group flex items-start gap-4 p-4 rounded-lg border border-border bg-card hover:border-primary/50 hover:bg-accent/50 transition-colors"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <link.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-medium text-foreground group-hover:text-primary transition-colors">
                      {link.title}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {link.description}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 mt-1" />
                </Link>
              ))}
            </div>

            {/* CTA */}
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-4">
                Precisa de ajuda? Estamos disponíveis para esclarecer qualquer dúvida.
              </p>
              <Button asChild variant="outline">
                <Link to="/faq">
                  Consultar Perguntas Frequentes
                </Link>
              </Button>
            </div>
          </div>
        </main>

        <Footer variant="full" />
      </div>
    </>
  );
};

export default NotFound;
